import { ProductAttributes } from '@app/components/products/ProductAttributes';
import { ProductPrice } from '@app/components/products/ProductPrice';
import { ProductSummary } from '@app/components/products/ProductSummary/ProductSummary';
import { UnitSwitcher } from '@app/components/products/UnitSwitcher';
import { Button } from '@app/components/various/Button';
import { CustomImage } from '@app/components/various/CustomImage';
import { Remove } from '@app/components/various/Icons';
import { ProductBadge } from '@app/components/various/ProductBadge';
import { Tooltip } from '@app/components/various/Tooltip';
import { createProductImageSelector, getDefaultImageAspectRatio } from '@app/ducks/helpers';
import { getOrderCurrency, getProductInfo } from '@app/ducks/order';
import { formatPriceWithCurrencyAffix } from '@app/logic/price';
import { getUrlForProductAndVariant } from '@app/logic/products';
import { isSwitchAvailable } from '@app/logic/unitsSwitch';
import { cn } from '@app/utils/cn';
import { Product } from '@typings';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';

import { SummaryValues } from './OrderProduct';

interface Props {
  productInfo: Product.Standard;
  onRemove: VoidFunction;
  summaryValues: SummaryValues;
  isDisabled: boolean;
  isRemoving: boolean;
  isMutatingUnits: boolean;
  hasEtaBadge: boolean;
}

const getMedia = createProductImageSelector(getProductInfo);

export const OrderProductInfo = (props: Props) => {
  const { productInfo, summaryValues, isDisabled, isRemoving, isMutatingUnits, hasEtaBadge } = props;
  const { t } = useTranslation(['common', 'products', 'selections']);
  const productId = productInfo.product;
  const variantId = productInfo.variant;
  const currency = useSelector(getOrderCurrency);
  const aspectRatio = useSelector(getDefaultImageAspectRatio);
  const imageSrc = useSelector((state: Store) => getMedia(state, productId)).src;
  const link = !isDisabled ? getUrlForProductAndVariant({ productId, variantId }) : undefined;

  return (
    <div className={cn('flex flex-row gap-x-8 gap-y-6 pb-5', { 'pointer-events-none': isDisabled })}>
      <div className={cn('row-span-2 size-full w-32 shrink-0', { 'opacity-50': isDisabled })}>
        <div className="relative">
          <Link to={link ?? '#'} className="block rounded-small focus-visible:focus-outline">
            <CustomImage
              aspectRatio={aspectRatio}
              src={imageSrc}
              placeholderClassName="rounded-small overflow-hidden"
              imageClassName="bg-white"
            />
          </Link>
          {hasEtaBadge && (
            <div className={cn('pointer-events-none absolute inset-0 z-elevated w-fit max-w-full p-2')}>
              <ProductBadge badge={{ badgeType: 'eta' }} size="small" />
            </div>
          )}
        </div>
      </div>
      <div className="flex grow flex-col">
        <div className="flex items-start justify-between gap-4 pb-3">
          <div className="flex flex-1 flex-col justify-between gap-6 lg:flex-row lg:gap-4">
            <Link
              to={link ?? '#'}
              className={cn('block cursor-pointer focus-visible:rounded-small focus-visible:focus-outline', { 'opacity-50': isDisabled })}
            >
              <Tooltip overlayClassName="max-w-52" content={productInfo.name} disableOnTouchScreen>
                <h2 className="mb-1 line-clamp-2 body-1">{productInfo.name}</h2>
              </Tooltip>
              <ProductPrice productInfo={productInfo} />
            </Link>
          </div>
          <ProductSummary
            variantsCount={summaryValues.variantsCount}
            unitsCount={summaryValues.unitsCount}
            price={formatPriceWithCurrencyAffix(currency)(summaryValues.price)}
            disabled={isDisabled}
          />
        </div>
        <div className="pb-3">
          <ProductAttributes product={productInfo} isDisabled={isDisabled} isCompact />
        </div>
        <div className="pointer-events-auto flex items-center">
          <Button
            color="danger"
            variant="text"
            size="medium"
            icon={Remove}
            disabled={isMutatingUnits}
            isLoading={isRemoving}
            onClick={props.onRemove}
          >
            {isRemoving ? t('common:removing') : t('selections:remove_from_selection')}
          </Button>
        </div>
        {isSwitchAvailable(productInfo.tableMappings) && (
          <div className={cn('flex justify-end', { 'opacity-50': isDisabled })}>
            <UnitSwitcher tableMappings={productInfo.tableMappings} defaultUnit={productInfo.defaultLocalizedChart} />
          </div>
        )}
      </div>
    </div>
  );
};
